import * as api from "./client";

export type DashboardKind = "omni" | "tableau";
export type DashboardCategory = "submissions_analysis" | "client_experience" | "historical_data";
export type RequiredProduct = "sars" | "cxi_report" | "historical_data";
export type DashboardStatus = "draft" | "published" | "archived";

export const DASHBOARD_KINDS: readonly DashboardKind[] = ["omni", "tableau"];
export const DASHBOARD_CATEGORIES: readonly DashboardCategory[] = [
  "submissions_analysis",
  "client_experience",
  "historical_data",
];
export const REQUIRED_PRODUCTS: readonly RequiredProduct[] = [
  "sars",
  "cxi_report",
  "historical_data",
];
export const DASHBOARD_STATUSES: readonly DashboardStatus[] = ["draft", "published", "archived"];

export const HISTORICAL_DATA_REGION_IDS = [
  "UK",
  "UK_BAR",
  "US",
  "EMEA",
  "APAC",
  "LATAM",
  "Canada",
  "Caribbean",
  "Deutschland",
] as const;
export type HistoricalDataRegionId = (typeof HISTORICAL_DATA_REGION_IDS)[number];

export type AdminDashboard = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  kind: DashboardKind;
  category: DashboardCategory;
  required_product: RequiredProduct;
  region_id: string | null;
  edition: string | null;
  status: DashboardStatus;
  sort_order: number;
  omni_content_id: string | null;
  omni_link_access: string[];
  omni_custom_theme_id: string | null;
  tableau_host: string | null;
  tableau_view_path: string | null;
  legacy_wp_post_id: number | null;
  configurationIssues: string[];
  created_at: string;
  updated_at: string;
};

export type AdminDashboardInput = Omit<
  AdminDashboard,
  "id" | "created_at" | "updated_at" | "legacy_wp_post_id" | "configurationIssues"
>;

export type AdminDashboardsResponse = {
  dashboards: AdminDashboard[];
};

export type DashboardListFilters = {
  kind?: DashboardKind;
  status?: DashboardStatus;
};

export type DashboardPreview = {
  kind: DashboardKind;
  url: string;
};

const BASE = "/v1/data-products/admin/dashboards";

export const dataProducts = {
  list: async (filters?: DashboardListFilters): Promise<AdminDashboardsResponse> => {
    const params = new URLSearchParams();
    if (filters?.kind) params.append("kind", filters.kind);
    if (filters?.status) params.append("status", filters.status);
    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<AdminDashboardsResponse>(`${BASE}${query}`);
  },

  get: async (id: string): Promise<AdminDashboard> => {
    return api.get<AdminDashboard>(`${BASE}/${encodeURIComponent(id)}`);
  },

  create: async (input: AdminDashboardInput): Promise<AdminDashboard> => {
    return api.post<AdminDashboard>(BASE, input);
  },

  update: async (id: string, input: AdminDashboardInput): Promise<AdminDashboard> => {
    return api.put<AdminDashboard>(`${BASE}/${encodeURIComponent(id)}`, input);
  },

  remove: async (id: string): Promise<void> => {
    await api.del<void>(`${BASE}/${encodeURIComponent(id)}`);
  },

  preview: async (id: string): Promise<DashboardPreview> => {
    return api.get<DashboardPreview>(`${BASE}/${encodeURIComponent(id)}/preview`);
  },
};
